import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import dynamicFormService from '../../../../service/dynamicForm.service';
import {
  InputUI,
  SelectUI,
  DatePickerUI,
  AutoCompleteSelectUI,
  CheckboxGroupUI,
  DialogUI,
  ButtonUI,
} from '../../../Interface';
import ContactSignature from '../../../DynamicForm/ContactSignature';

import masterDataService from '../../../../service/masterData.service';
import { Box, Container, Grid, Typography } from '@mui/material';
import companyService from '../../../../service/company.service';
import simpletaskService from '../../../../service/simpletask.service';
import addNotification from '../../../../utils/addNotification';
import { NotificationType } from '../../../../config';
import {
  getDynamicFormAutocompleteValue,
  getDynamicFormFieldOptions,
  getDynamicFormInitialValues,
} from '../../../../utils/dynamicFormHelper';

const Worksheet = () => {
  const { dynFormId } = useParams();
  const [formsData, setFormsData] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [fields, setFields] = useState([]);
  const [formValues, setValues] = useState({});
  const [projectsList, setProjectsList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [simpleTasks, setSimpleTasks] = useState([]);
  const [simpleTask, setSimpleTask] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactSignatures, setContactSignatures] = useState({});
  const [customerSignature, setCustomerSignature] = useState('');
  const [userSignature, setUserSignature] = useState('');
  const [signatureResetKey, setSignatureResetKey] = useState(0);
  const [dynamicFormHeadId, setDynamicFormHeadId] = useState(null);


  // Contactok kiválasztása dialógusban
  const handleOpenContactDialog = () => setContactDialogOpen(true);
  const handleCloseContactDialog = () => setContactDialogOpen(false);
  const handleContactChange = (checkedIds) => {
    setSelectedContacts(checkedIds);
  };

  // Egy contact aláírásának mentése
  const handleSignatureChange = (contactId, dataUrl) => {
    setContactSignatures((prev) => ({ ...prev, [contactId]: dataUrl }));
  };

  useEffect(() => {
    masterDataService.getDynamicFroms().then((data) => {
      setFormsData(data);
    });

    masterDataService.projectToForm().then((data) => {
      setProjectsList(
        data.map((project) => ({
          label: project.projectName,
          value: project.projectId,
        }))
      );
      setProjects(data);
    });

    simpletaskService.getSimpleTasks().then((data) => {
      setSimpleTasks(data || []);
    });

  }, []);

  useEffect(() => {
    if (!dynFormId) return;

    const selectedDynamicForm = formsData.find((form) => form.id === Number(dynFormId));
    setSelectedForm(selectedDynamicForm ?? null);
  }, [dynFormId, formsData]);

  useEffect(() => {
    if (project?.projectId) {
      if (!project.companyId) {
        setContacts([]);
        setSelectedContacts([]);
        addNotification({
          content: 'A kiválasztott projekt nem rendelkezik partnerrel!',
          type: NotificationType.WARNING,
        });
        return;
      }

      companyService.getCompanyById(project.companyId).then((data) => {
        setContacts(
          (data?.companyContact || []).map((contact) => ({
            label: contact.name,
            value: contact.id,
          }))
        );
      });
    } else {
      setContacts([]);
      setSelectedContacts([]);
    }
  }, [project]);

  useEffect(() => {
    if (selectedForm?.id) {
      const getDynamicForm = dynFormId ? dynamicFormService.getWorksheet : dynamicFormService.getItem;
      getDynamicForm(selectedForm.id).then((data) => {
        const dynamicFormFields = data?.dynamicFormField || [];
        setFields(dynamicFormFields);
        setValues(getDynamicFormInitialValues(dynamicFormFields));
      });
    } else {
      setFields([]);
      setValues({});
    }
  }, [dynFormId, selectedForm]);

  const setFieldValue = (fieldId, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [fieldId]: value,
    }));
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'TEXT':
      case 'LONG_TEXT':
        return (
          <InputUI
            label={field.name}
            value={formValues?.[field.id] || ''}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            multiline={field.type === 'LONG_TEXT'}
            rows={field.type === 'LONG_TEXT' ? 3 : 1}
            variant="standard"
          />
        );
      case 'DECIMAL':
        return (
          <InputUI
            label={field.name}
            type="number"
            value={formValues?.[field.id] || ''}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            variant="standard"
          />
        );
      case 'COMBO':
        if (field.autocomplete || field.multiselect) {
          return (
            <AutoCompleteSelectUI
              label={field.name}
              options={getDynamicFormFieldOptions(field)}
              value={getDynamicFormAutocompleteValue(field, formValues?.[field.id])}
              multiple={field.multiselect}
              onChange={(_e, newValue) =>
                setFieldValue(
                  field.id,
                  field.multiselect ? (newValue || []).map((option) => option.value) : newValue?.value ?? null
                )
              }
              variant="standard"
            />
          );
        }
        return (
          <SelectUI
            label={field.name}
            value={formValues?.[field.id] ?? ''}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            option={getDynamicFormFieldOptions(field)}
            variant="standard"
          />
        );
      case 'DATE':
        return (
          <DatePickerUI
            label={field.name}
            value={formValues?.[field.id] || null}
            onChange={(val) => setFieldValue(field.id, val)}
            variant="standard"
          />
        );
      case 'STATIC':
        return <Typography style={{ padding: '8px 0', fontStyle: 'italic' }}>{field.staticText}</Typography>;
      default:
        return (
          <InputUI
            label={field.name}
            value={formValues?.[field.id] || ''}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            variant="standard"
          />
        );
    }
  };

  // Mentés
  const handleSave = async (preview = false) => {
    const selectedContactSignatures = Object.fromEntries(
      Object.entries(contactSignatures).filter(([contactId]) => selectedContacts.includes(contactId))
    );
    const payload = {
      formId: selectedForm?.id,
      projectId: project?.projectId,
      simpleTaskId: simpleTask?.simpleTaskId,
      values: formValues,
      signatures: selectedContactSignatures,
      customerSignature: selectedContacts.length === 0 ? customerSignature : null,
      userSignature,
      dynamicFormHeadId,
      isPreview: preview,
    };
    dynamicFormService.addForm(payload).then((data) => {
      addNotification({ content: 'Sikeres mentés!', type: NotificationType.SUCCESS });
      // Reset állapotok
      if (!preview) {
        setValues({});
        if (!dynFormId) setSelectedForm(null);
        setProject(null);
        setSimpleTask(null);
        setSelectedContacts([]);
        setContactSignatures({});
        setCustomerSignature('');
        setUserSignature('');
        setSignatureResetKey((currentKey) => currentKey + 1);
        if (!dynFormId) setFields([]);
        setDynamicFormHeadId(null);
        return;
      }

      addNotification({ content: 'Előnézet elkészült!', type: NotificationType.INFO });
      setDynamicFormHeadId(data.headId);

      if (data?.templatePath) {
        const url = `${process.env.REACT_APP_API_URL}/${data.templatePath}`;
        window.open(url, '_blank');
      }
    });
  };

  return (
    <>
      <Container maxWidth="md">
        <Grid mt={2} container>
          <Grid item xs={12} mb={2}>
            <AutoCompleteSelectUI
              label="Projekt kiválasztása"
              options={projectsList}
              value={projectsList.find((p) => p.value === project?.projectId) || null}
              onChange={(_e, newVal) => {
                setProject(newVal?.value ? projects.find((proj) => proj.projectId === newVal.value) : null);
                setSimpleTask(null);
              }}
              placeholder="Válassz projektet"
            />
          </Grid>

          <Grid item xs={12} mb={2}>
            <AutoCompleteSelectUI
              label="Feladat kiválasztása (opcionális)"
              options={simpleTasks
                .filter((task) => task.projectId === project?.projectId)
                .map((task) => ({
                  label: task.simpleTaskName,
                  value: task.simpleTaskId,
                }))}
              value={
                simpleTask
                  ? {
                      label: simpleTask.simpleTaskName,
                      value: simpleTask.simpleTaskId,
                    }
                  : null
              }
              onChange={(_e, newVal) =>
                setSimpleTask(
                  newVal?.value
                    ? simpleTasks.find((task) => task.simpleTaskId === newVal.value) ?? null
                    : null
                )
              }
              placeholder="Válassz feladatot (opcionális)"
              disabled={!project?.projectId}
            />
          </Grid>

          <Grid item xs={12} mt={2}>
            <ButtonUI
              text="Kapcsolattartók kiválasztása"
              color="blue-gray"
              variant="gradient"
              onClick={handleOpenContactDialog}
              disabled={!project?.projectId}
            />
            {selectedContacts.length > 0 && (
              <div className="mt-2 text-sm text-gray-700">
                Kiválasztva:{' '}
                {contacts
                  .filter((c) => selectedContacts.includes(c.value.toString()))
                  .map((c) => c.label)
                  .join(', ')}
              </div>
            )}
          </Grid>

          {fields.length > 0 && (
            <>
              {fields.map((field) => (
                <Grid item xs={12} mt={1} key={field.id}>
                  {renderField(field)}
                </Grid>
              ))}
            </>
          )}
        </Grid>

        {/* Megrendelői aláírás a form után */}
        <Box mt={6}>
          <h3 style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Megrendelői aláírás</h3>
          {selectedContacts.length > 0 ? (
            contacts
              .filter((c) => selectedContacts.includes(c.value.toString()))
              .map((contact) => (
                <ContactSignature
                  key={`${signatureResetKey}-${contact.value}`}
                  contact={contact}
                  value={contactSignatures[contact.value]}
                  onChange={handleSignatureChange}
                />
              ))
          ) : (
            <ContactSignature
              key={`customer-${signatureResetKey}`}
              contact={{ label: 'Megrendelő', value: 'customer' }}
              value={customerSignature}
              onChange={(_contactId, dataUrl) => setCustomerSignature(dataUrl)}
            />
          )}
        </Box>

        {/* A saját oldalon mindig a bejelentkezett felhasználó ír alá. */}
        <Box mt={6}>
          <h3 style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Saját oldali aláírás</h3>
          <ContactSignature
            key={`user-${signatureResetKey}`}
            contact={{ label: 'Aláírás', value: 'currentUser' }}
            value={userSignature}
            onChange={(_userId, dataUrl) => setUserSignature(dataUrl)}
          />
        </Box>

        {/* Mentés gomb */}
        <Box mt={6} display="flex" justifyContent="center">
          <ButtonUI
            text="Mentés"
            color="green"
            variant="gradient"
            onClick={() => handleSave(false)}
            disabled={!selectedForm || !project}
          />
          <ButtonUI
            style={{ marginLeft: '2rem' }}
            text="Előnézet"
            color="blue"
            variant="gradient"
            onClick={() => handleSave(true)}
            disabled={!selectedForm || !project}
          />
        </Box>
      </Container>
      {/* Contact kiválasztó dialógus */}
      <DialogUI
        open={contactDialogOpen}
        onClose={handleCloseContactDialog}
        headerContent="Kapcsolattartók kiválasztása"
        fullWidth={true}
        maxWidth="md"
      >
        <CheckboxGroupUI options={contacts} value={selectedContacts} onChange={handleContactChange} mode="multi" />
        <Box display="flex" justifyContent="center" mt={4}>
          <ButtonUI text="Kész" onClick={handleCloseContactDialog} className="bg-success" />
        </Box>
      </DialogUI>

    </>
  );
};

export default Worksheet;
